import {
  Body,
  Button,
  Chip,
  Heading,
  IconButton,
} from '@assembly-js/design-system';
import { FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';

import {
  AppNotificationConfig,
  NotificationSurface,
} from 'src/constants/dataTypes/appsTypes';
import {
  NOTIFICATION_SURFACES,
  type NotificationSettingDraft,
  toNotificationSetting,
} from 'src/features/app-notification-settings/notificationConfig';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { NotificationSettingModal } from 'src/routes/appSetup/builder/settings/pages/notifications/NotificationSettingModal';

// Leading-dot accent colors for the surface pills.
const SURFACE_DOT_COLOR: Record<NotificationSurface, string> = {
  product: '#4263EB',
  email: '#2F9E44',
};

// Only the slice of the form this section reads/writes — the parent Formik schema owns the
// full shape, so we keep this loose and touch notificationConfig (plus the app name) only.
interface NotificationConfigFormValues {
  name?: string;
  notificationConfig?: AppNotificationConfig;
}

export function NotificationSettingsSection() {
  const { values, handleChange, handleBlur } =
    useFormikContext<NotificationConfigFormValues>();
  const notifications = values.notificationConfig?.notifications ?? [];
  const actionLabel = values.notificationConfig?.actionLabel;
  const appName = values.name?.trim() || 'this app';

  // index === null while adding a new setting; a number while editing an existing one.
  const [editor, setEditor] = useState<{ open: boolean; index: number | null }>(
    {
      open: false,
      index: null,
    },
  );
  const closeEditor = () => setEditor({ open: false, index: null });

  return (
    <>
      <div className="border-0 border-b border-solid border-non-hover-border py-6 mb-4">
        <Heading size="lg">Configure notifications</Heading>
        <Body size="sm" className="text-gray-small mt-2">
          Allow internal users who have your app installed to configure whether
          they receive the app&apos;s notifications. Each entry below becomes a
          row on every internal user&apos;s notification settings page, where
          they can toggle product and email delivery.
        </Body>
      </div>

      <FieldArray name="notificationConfig.notifications">
        {({ push, replace, remove }) => {
          const handleSave = (draft: NotificationSettingDraft) => {
            // Preserve an existing setting's id; the backend stamps new ones.
            const nextSetting = toNotificationSetting(
              draft,
              editor.index != null ? notifications[editor.index].id : '',
            );
            if (editor.index != null) {
              replace(editor.index, nextSetting);
            } else {
              push(nextSetting);
            }
            closeEditor();
          };

          return (
            <div className="mb-6">
              <div className="border border-solid border-non-hover-border rounded-lg overflow-hidden">
                <div className="flex items-start justify-between gap-4 bg-gray-100 px-4 py-3 border-0 border-b border-solid border-non-hover-border">
                  <div>
                    <BaseTypography fontType="13Medium">
                      Notification settings
                    </BaseTypography>
                    <Body size="sm" className="text-gray-small">
                      App workspace name shown beneath each row:{' '}
                      <span className="font-medium text-black-heading">
                        {appName}
                      </span>
                    </Body>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    prefixIcon="Plus"
                    label="Add notification setting"
                    onClick={() => setEditor({ open: true, index: null })}
                    className="whitespace-nowrap"
                  />
                </div>

                {notifications.length === 0 ? (
                  <div className="px-4 py-6">
                    <Body size="sm" className="text-gray-small">
                      No notification settings yet. Add one to let internal
                      users control it.
                    </Body>
                  </div>
                ) : (
                  notifications.map((setting, index) => (
                    <div
                      key={`notification-setting-${index}`}
                      className={`flex items-center justify-between gap-4 px-4 py-3 ${
                        index < notifications.length - 1
                          ? 'border-0 border-b border-solid border-non-hover-border'
                          : ''
                      }`}
                    >
                      <div className="min-w-0">
                        <Body className="text-black-heading truncate">
                          {setting.label}
                        </Body>
                        <Body size="sm" className="text-gray-small truncate">
                          {appName}
                        </Body>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {NOTIFICATION_SURFACES.filter((surface) =>
                          setting.surfaces.includes(surface.id),
                        ).map((surface) => (
                          <Chip
                            key={surface.id}
                            label={surface.label}
                            variant="outlined"
                            size="sm"
                            prefixIcon={
                              <div
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                  backgroundColor:
                                    SURFACE_DOT_COLOR[surface.id],
                                }}
                              />
                            }
                          />
                        ))}
                        <IconButton
                          icon="Edit"
                          variant="minimal"
                          label="Edit notification setting"
                          onClick={() => setEditor({ open: true, index })}
                        />
                        <IconButton
                          icon="Trash"
                          variant="minimal"
                          label="Delete notification setting"
                          onClick={() => remove(index)}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {editor.open && (
                <NotificationSettingModal
                  initialSetting={
                    editor.index != null ? notifications[editor.index] : null
                  }
                  onClose={closeEditor}
                  onSave={handleSave}
                />
              )}
            </div>
          );
        }}
      </FieldArray>

      <div className="mb-2">
        <Body size="sm" className="font-medium text-black-heading">
          Action label
        </Body>
        <Body size="sm" className="text-gray-small mb-3">
          Used by surfaces that summarize pending items as &ldquo;[verb] [count]
          [noun]&rdquo; (e.g. &ldquo;Review 3 documents&rdquo;).
        </Body>
        <div className="flex gap-2">
          <BaseTextField
            className="flex-1"
            fullWidth
            label={<Body size="sm">Verb</Body>}
            name="notificationConfig.actionLabel.verb"
            value={actionLabel?.verb ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            placeholder="Review"
            sizeVariant="medium"
            variant="outlined"
          />
          <BaseTextField
            className="flex-1"
            fullWidth
            label={<Body size="sm">Singular noun</Body>}
            name="notificationConfig.actionLabel.singularNoun"
            value={actionLabel?.singularNoun ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            placeholder="document"
            sizeVariant="medium"
            variant="outlined"
          />
          <BaseTextField
            className="flex-1"
            fullWidth
            label={<Body size="sm">Plural noun</Body>}
            name="notificationConfig.actionLabel.pluralNoun"
            value={actionLabel?.pluralNoun ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            placeholder="documents"
            sizeVariant="medium"
            variant="outlined"
          />
        </div>
      </div>
    </>
  );
}
